/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CButton, CSpinner, CAlert, CFormSelect
} from '@coreui/react';
import { CIcon } from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';
import { getWeaveDetail } from '../../services/endpoints';
import './ProductInputs.scss';
import SearchableSelect from '../SearchableSelect/SearchableSelect';

const ProductInputs = ({
  onSubmit,
  onCancel,
  isLoading = false,
  submitResponse = null,
  initialData = null,
  isEdit = false,
  weaveOptions = [],
  materialOptions = [],
  yarnOptions = [],
  colorOptions = [],
  dropdownLoading = false,
  dropdownError = null,
  onMaterialChange = null,
  onWeaveSearch = null
}) => {
  const [formData, setFormData] = useState({
    PrdWeavesGuid: '',
    PrdMaterialGuid: '',
    PrdYarnGuid: '',
    PrdColorGuid: '',
    Certificate: '',
    Lot: '',
    Description: '',
    // Technical fields
    WarpDensity: '',
    WarpThreadCount: '',
    ReedNo: '',
    DGTS: '',
    ReedWidth: '',
    WeftDensity: '',
    FabricWidth: '',
    FabricWeight: '',
    WeaveTypeName: '',
    ThreadName: ''
  });
  const [errors, setErrors] = useState({});
  const [weaveDetail, setWeaveDetail] = useState(null);
  const [weaveDetailLoading, setWeaveDetailLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        PrdWeavesGuid: initialData.PrdWeavesGuid || '',
        PrdMaterialGuid: initialData.PrdMaterialGuid || '',
        PrdYarnGuid: initialData.PrdYarnGuid || '',
        PrdColorGuid: initialData.PrdColorGuid || '',
        Certificate: initialData.Certificate || '',
        Lot: initialData.Lot || '',
        Description: initialData.Description || '',
        // Technical fields
        WarpDensity: initialData.WarpDensity || '',
        WarpThreadCount: initialData.WarpThreadCount || '',
        ReedNo: initialData.ReedNo || '',
        DGTS: initialData.DGTS || '',
        ReedWidth: initialData.ReedWidth || '',
        WeftDensity: initialData.WeftDensity || '',
        FabricWidth: initialData.FabricWidth || '',
        FabricWeight: initialData.FabricWeight ? (initialData.FabricWeight / 100).toFixed(2) : '',
        WeaveTypeName: initialData.WeaveTypeName || '',
        ThreadName: initialData.ThreadName || ''
      });

      // If weave is selected in initial data, fetch weave detail
      if (initialData.PrdWeavesGuid) {
        fetchWeaveDetail(initialData.PrdWeavesGuid);
      }
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            PrdWeavesGuid: '',
            PrdMaterialGuid: '',
            PrdYarnGuid: '',
            PrdColorGuid: '',
            Certificate: '',
            Lot: '',
            Description: '',
            // Technical fields
            WarpDensity: '',
            WarpThreadCount: '',
            ReedNo: '',
            DGTS: '',
            ReedWidth: '',
            WeftDensity: '',
            FabricWidth: '',
            FabricWeight: '',
            WeaveTypeName: '',
            ThreadName: ''
          });
        }
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // If material is changed and new material does NOT start with 'i', clear related selections
    if (name === 'PrdMaterialGuid') {
      const selectedMaterial = materialOptions.find(m => m.PrdMaterialGuid === value);
      const isNewMaterialIMaterial = selectedMaterial?.MaterialName?.toLowerCase().startsWith('i');

      if (!isNewMaterialIMaterial) {
        setFormData(prev => ({
          ...prev,
          PrdWeavesGuid: '',
          PrdYarnGuid: '',
          PrdColorGuid: '',
          // Clear technical fields when material switches away from 'i'
          WarpDensity: '',
          WarpThreadCount: '',
          ReedNo: '',
          DGTS: '',
          ReedWidth: '',
          WeftDensity: '',
          FabricWidth: '',
          FabricWeight: '',
          WeaveTypeName: '',
          ThreadName: ''
        }));
        const clearedErrors = { ...errors };
        if (clearedErrors.PrdWeavesGuid) clearedErrors.PrdWeavesGuid = '';
        if (clearedErrors.PrdYarnGuid) clearedErrors.PrdYarnGuid = '';
        if (clearedErrors.PrdColorGuid) clearedErrors.PrdColorGuid = '';
        setErrors(clearedErrors);
        // Clear weave detail when material is not 'i'
        setWeaveDetail(null);
      }

      // Notify parent component about material change
      if (onMaterialChange) {
        onMaterialChange(value, isNewMaterialIMaterial);
      }
    }

    // If weave is changed, fetch weave details
    if (name === 'PrdWeavesGuid' && value) {
      fetchWeaveDetail(value);
    } else if (name === 'PrdWeavesGuid' && !value) {
      // Clear weave detail when weave is deselected
      setWeaveDetail(null);
      // Clear technical fields
      setFormData(prev => ({
        ...prev,
        WarpDensity: '',
        WarpThreadCount: '',
        ReedNo: '',
        DGTS: '',
        ReedWidth: '',
        WeftDensity: '',
        FabricWidth: '',
        FabricWeight: '',
        WeaveTypeName: '',
        ThreadName: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.PrdMaterialGuid.trim()) newErrors.PrdMaterialGuid = t('prdMaterialGuidRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (!userId || userId === 'undefined') {
        setErrors({ general: 'User session not found. Please login again.' });
        return;
      }
      const submitData = {
        SessionUserId: String(userId),
        PrdMaterialGuid: String(formData.PrdMaterialGuid || '').trim(),
        PrdYarnGuid: String(formData.PrdYarnGuid || '').trim(),
        PrdColorGuid: String(formData.PrdColorGuid || '').trim(),
        Certificate: String(formData.Certificate || '').trim(),
        Lot: String(formData.Lot || '').trim(),
        Description: String(formData.Description || '').trim(),
        // Technical fields
        WarpThreadCount: String(formData.WarpThreadCount || '').trim(),
        ReedNo: String(formData.ReedNo || '').trim(),
        DGTS: String(formData.DGTS || '').trim(),
        ReedWidth: String(formData.ReedWidth || '').trim(),
        WeftDensity: String(formData.WeftDensity || '').trim()
      };

      // Include PrdWeavesGuid when a weave is selected
      if (formData.PrdWeavesGuid) {
        submitData.PrdWeavesGuid = String(formData.PrdWeavesGuid).trim();
      }
      if (isEdit && initialData?.PrdProductGuid) {
        submitData.ProductId = initialData.PrdProductGuid;
      }
      onSubmit(submitData);
    }
  };

  const handleCancel = () => {
    if (!isEdit) {
      setFormData({
        PrdWeavesGuid: '',
        PrdMaterialGuid: '',
        PrdYarnGuid: '',
        PrdColorGuid: '',
        Certificate: '',
        Lot: '',
        Description: '',
        // Technical fields
        WarpDensity: '',
        WarpThreadCount: '',
        ReedNo: '',
        DGTS: '',
        ReedWidth: '',
        WeftDensity: '',
        FabricWidth: '',
        FabricWeight: '',
        WeaveTypeName: '',
        ThreadName: ''
      });
    }
    setErrors({});
    onCancel();
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateProduct') : t('createProduct');
  };

  const formatDensityDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numericValue = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(numericValue)) return value;
    const scaledValue = Math.round(numericValue);
    const whole = Math.trunc(scaledValue / 100);
    const decimal = Math.abs(scaledValue % 100).toString().padStart(2, '0');
    return `${whole},${decimal}`;
  };
  const fetchWeaveDetail = async (weaveGuid) => {
    setWeaveDetailLoading(true);
    try {
      const sessionUserId = localStorage.getItem('sessionUserId');
      const response = await getWeaveDetail({
        SessionUserId: sessionUserId,
        WeaveId: weaveGuid
      });

      if (response?.data?.WeaveList && response.data.WeaveList.length > 0) {
        const weave = response.data.WeaveList[0];
        setWeaveDetail(weave);

        // Map weave data to form fields
        setFormData(prev => ({
          ...prev,
          WarpDensity: weave.WarpDensity || '',
          WarpThreadCount: weave.TotalThreads || '',
          ReedNo: weave.ReedNo || '',
          DGTS: weave.Dgts || '', // API'de Dgts olarak geliyor
          ReedWidth: weave.ReedWidth || '',
          WeftDensity: weave.WeftDensity || '',
          FabricWidth: weave.FabricWidth || '',
          FabricWeight: weave.FabricWeight ? (weave.FabricWeight / 100).toFixed(2) : '',
          WeaveTypeName: weave.WeaveTypeName || '',
          ThreadName: weave.ThreadName || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching weave detail:', error);
    } finally {
      setWeaveDetailLoading(false);
    }
  };

  // Check if selected material starts with 'i'
  const selectedMaterial = materialOptions.find(m => m.PrdMaterialGuid === formData.PrdMaterialGuid);
  const isIMaterial = selectedMaterial?.MaterialName?.toLowerCase().startsWith('i');

  return (
    <div className="product-inputs-container">
      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}
      {submitResponse?.Result?.ErrorCode !== 0 && submitResponse?.Result?.ErrorDescription && (
        <CAlert color="danger">{submitResponse.Result.ErrorDescription}</CAlert>
      )}
      <CForm onSubmit={handleSubmit} noValidate>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="PrdMaterialGuid">{t('material')}</CFormLabel>
            <CFormSelect
              id="PrdMaterialGuid"
              name="PrdMaterialGuid"
              value={formData.PrdMaterialGuid}
              onChange={handleInputChange}
              invalid={!!errors.PrdMaterialGuid}
              disabled={isLoading || dropdownLoading || isEdit}
            >
              <option value="">{dropdownLoading ? t('loading') : t('select')}</option>
              {materialOptions.map((item) => (
                <option key={item.PrdMaterialGuid} value={item.PrdMaterialGuid}>
                  {item.MaterialName}
                </option>
              ))}
            </CFormSelect>
            {errors.PrdMaterialGuid && <div className="invalid-feedback d-block">{errors.PrdMaterialGuid}</div>}
          </CCol>
          {!isIMaterial && formData.PrdMaterialGuid && (
            <CCol md={6}>
              <CFormLabel htmlFor="PrdWeavesGuid">{t('weave')}</CFormLabel>
              <SearchableSelect
                id="PrdWeavesGuid"
                name="PrdWeavesGuid"
                options={weaveOptions}
                value={formData.PrdWeavesGuid}
                onChange={(selectedValue) => handleInputChange({ target: { name: 'PrdWeavesGuid', value: selectedValue } })}
                onSearchTermChange={onWeaveSearch}
                placeholder={dropdownLoading ? t('loading') : t('select')}
                disabled={isLoading || dropdownLoading}
                invalid={!!errors.PrdWeavesGuid}
                displayKey="WeaveCode"
                valueKey="PrdWeavesGuid"
                emptyText={t('No options found')}
              />
              {errors.PrdWeavesGuid && <div className="invalid-feedback d-block">{errors.PrdWeavesGuid}</div>}
            </CCol>
          )}
        </CRow>

        {/* Yarn and Color Dropdowns - only when material starts with 'i' */}
        {isIMaterial && (
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="PrdYarnGuid">{t('yarn')}</CFormLabel>
              <SearchableSelect
                id="PrdYarnGuid"
                name="PrdYarnGuid"
                options={yarnOptions.map(item => ({
                  Guid: item.PrdYarnGuid,
                  Name: item.YarnCode ? `${item.YarnCode} - ${item.YarnName}` : item.YarnName
                }))}
                value={formData.PrdYarnGuid}
                onChange={(selectedValue) => handleInputChange({ target: { name: 'PrdYarnGuid', value: selectedValue } })}
                placeholder={dropdownLoading ? t('loading') : t('selectYarn')}
                disabled={isLoading || dropdownLoading}
                displayKey="Name"
                valueKey="Guid"
                emptyText={t('No yarns found')}
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel htmlFor="PrdColorGuid">{t('color')}</CFormLabel>
              <SearchableSelect
                id="PrdColorGuid"
                name="PrdColorGuid"
                options={colorOptions}
                value={formData.PrdColorGuid}
                onChange={(selectedValue) => handleInputChange({ target: { name: 'PrdColorGuid', value: selectedValue } })}
                placeholder={dropdownLoading ? t('loading') : t('selectColor')}
                disabled={isLoading || dropdownLoading}
                displayKey="ColorName"
                valueKey="PrdColorGuid"
                emptyText={t('No colors found')}
              />
            </CCol>
          </CRow>
        )}
        {isIMaterial ? (
          <CRow className="mb-3">
            <CCol md={4}>
              <CFormLabel htmlFor="Certificate">{t('certificate')}</CFormLabel>
              <CFormInput
                type="text"
                id="Certificate"
                name="Certificate"
                value={formData.Certificate}
                onChange={handleInputChange}
                invalid={!!errors.Certificate}
                placeholder={t('enterCertificate')}
                disabled={isLoading}
              />
              {errors.Certificate && <div className="invalid-feedback d-block">{errors.Certificate}</div>}
            </CCol>
            <CCol md={4}>
              <CFormLabel htmlFor="Lot">{t('lot')}</CFormLabel>
              <CFormInput
                type="text"
                id="Lot"
                name="Lot"
                value={formData.Lot}
                onChange={handleInputChange}
                invalid={!!errors.Lot}
                placeholder={t('enterLot')}
                disabled={isLoading}
              />
              {errors.Lot && <div className="invalid-feedback d-block">{errors.Lot}</div>}
            </CCol>
            <CCol md={4}>
              <CFormLabel htmlFor="Description">{t('description')}</CFormLabel>
              <CFormInput
                type="text"
                id="Description"
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                invalid={!!errors.Description}
                placeholder={t('enterDescription')}
                disabled={isLoading}
              />
              {errors.Description && <div className="invalid-feedback d-block">{errors.Description}</div>}
            </CCol>
          </CRow>
        ) : (
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel htmlFor="Certificate">{t('certificate')}</CFormLabel>
              <CFormInput
                type="text"
                id="Certificate"
                name="Certificate"
                value={formData.Certificate}
                onChange={handleInputChange}
                invalid={!!errors.Certificate}
                placeholder={t('enterCertificate')}
                disabled={isLoading}
              />
              {errors.Certificate && <div className="invalid-feedback d-block">{errors.Certificate}</div>}
            </CCol>
          </CRow>
        )}

        {/* Technical fields - only show if material name doesn't start with 'i' */}
        {!isIMaterial && formData.PrdMaterialGuid && (
          <>
            {weaveDetailLoading && (
              <CRow className="mb-3">
                <CCol md={12}>
                  <div className="text-center">
                    <CSpinner size="sm" className="me-2" />
                    {t('loadingWeaveDetails')}
                  </div>
                </CCol>
              </CRow>
            )}
            <CRow className="mb-3">
              <CCol md={3}>
                <CFormLabel htmlFor="WarpDensity">{t('warpDensity')}</CFormLabel>
                <CFormInput
                  type="text"
                  id="WarpDensity"
                  name="WarpDensity"
                  value={formatDensityDisplay(formData.WarpDensity)}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterWarpDensity')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="WeftDensity">{t('weftDensity')}</CFormLabel>
                <CFormInput
                  type="text"
                  id="WeftDensity"
                  name="WeftDensity"
                  value={formatDensityDisplay(formData.WeftDensity)}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterWeftDensity')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="ReedWidth">{t('reedWidth')}</CFormLabel>
                <CFormInput
                  type="number"
                  id="ReedWidth"
                  name="ReedWidth"
                  value={formData.ReedWidth}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('enterReedWidth') : t('enterReedWidth')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="ReedNo">{t('reedNo')}</CFormLabel>
                <CFormInput
                  type="number"
                  id="ReedNo"
                  name="ReedNo"
                  value={formData.ReedNo}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterReedNo')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
            </CRow>
            <CRow className="mb-3">
              <CCol md={3}>
                <CFormLabel htmlFor="DGTS">{t('dgts')}</CFormLabel>
                <CFormInput
                  type="number"
                  id="DGTS"
                  name="DGTS"
                  value={formData.DGTS}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterDGTS')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="WarpThreadCount">{t('warpThreadCount')}</CFormLabel>
                <CFormInput
                  type="number"
                  id="WarpThreadCount"
                  name="WarpThreadCount"
                  value={formData.WarpThreadCount}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterWarpThreadCount')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="FabricWidth">{t('grayFabricWidth')}</CFormLabel>
                <CFormInput
                  type="number"
                  id="FabricWidth"
                  name="FabricWidth"
                  value={formData.FabricWidth}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterFabricWidth')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={3}>
                <CFormLabel htmlFor="FabricWeight">{t('grayFabricWeight')}</CFormLabel>
                <CFormInput
                  type="text"
                  id="FabricWeight"
                  name="FabricWeight"
                  value={formData.FabricWeight}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('enterFabricWeight')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
            </CRow>
            <CRow className="mb-3 weave-summary-row justify-content-center">
              <CCol md={4} className="weave-summary-col">
                <CFormLabel htmlFor="WeaveTypeName">{t('weaveTypeName')}</CFormLabel>
                <CFormInput
                  type="text"
                  id="WeaveTypeName"
                  name="WeaveTypeName"
                  value={formData.WeaveTypeName}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('weaveTypeName')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
              <CCol md={4} className="weave-summary-col">
                <CFormLabel htmlFor="ThreadName">{t('composition')}</CFormLabel>
                <CFormInput
                  type="text"
                  id="ThreadName"
                  name="ThreadName"
                  value={formData.ThreadName}
                  onChange={handleInputChange}
                  placeholder={weaveDetailLoading ? t('loading') : t('composition')}
                  disabled={true}
                  readOnly={true}
                />
              </CCol>
            </CRow>
          </>
        )}
        <CRow>
          <CCol md={12} className="d-flex gap-2">
            <CButton color="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

ProductInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
  weaveOptions: PropTypes.array,
  materialOptions: PropTypes.array,
  yarnOptions: PropTypes.array,
  colorOptions: PropTypes.array,
  dropdownLoading: PropTypes.bool,
  dropdownError: PropTypes.string,
  onMaterialChange: PropTypes.func,
  onWeaveSearch: PropTypes.func,
};

export default ProductInputs;
